import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent, normalizeNumber, getDevice, setFunnelPage } from "@/lib/analytics";
import { flushPendingLeads } from "@/lib/hcpLead";
import { reportConfirmedLeads, confirmAwaitingLead, loadGoogleAds } from "@/lib/googleAdsConversions";
import { captureAdClick, stampAdClickOnForm } from "@/lib/adClick";
import { initialiseLeadForms } from "@/lib/formLoad";

/**
 * Global, delegated conversion tracking:
 *  - click_to_call  → every tel: link/button on every page
 *  - click_to_text  → every sms: link
 *  - form_submit    → every lead form submission (Web3Forms native POST)
 *  - page_view      → SPA route changes
 *
 * Uses capture-phase document listeners so no per-component wiring is needed.
 */
const ConversionTracking = () => {
  const location = useLocation();
  useEffect(() => initialiseLeadForms(), []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest?.("a[href^='tel:'], a[href^='sms:']") as HTMLAnchorElement | null;
      if (!link) return;

      const href = link.getAttribute("href") || "";
      const isSms = href.toLowerCase().startsWith("sms:");
      const label =
        (link.textContent || "").trim().replace(/\s+/g, " ").slice(0, 80) ||
        link.getAttribute("aria-label") ||
        "";

      trackEvent(isSms ? "click_to_text" : "click_to_call", {
        phone_number: normalizeNumber(href),
        button_text: label,
        link_url: href,
        device: getDevice(),
        location_on_page: link.closest("header")
          ? "header"
          : link.closest("footer")
          ? "footer"
          : link.closest("[data-sticky-cta]")
          ? "sticky_mobile_bar"
          : "page_content",
      });
    };

    const onSubmit = (e: Event) => {
      const form = e.target as HTMLFormElement | null;
      if (!form || form.tagName !== "FORM") return;

      const action = form.getAttribute("action") || "";
      const isLeadForm = action.includes("web3forms.com") || !!form.querySelector("input[name='access_key']");
      if (!isLeadForm) return;

      // Stamp the stored ad click onto the form synchronously, before React
      // handlers and the native POST read the form data.
      stampAdClickOnForm(form);

      const getVal = (name: string) =>
        (form.querySelector(`[name='${name}']`) as HTMLInputElement | null)?.value || "";

      // Remember which page produced the lead so generate_lead (fired on
      // /thank-you after the Web3Forms redirect) reports the same funnel_page.
      setFunnelPage(window.location.pathname);

      // Submission is not acceptance: rejected spam must not fire a lead event.
    };

    document.addEventListener("click", onClick, true);
    document.addEventListener("submit", onSubmit, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, true);
    };
  }, []);

  // Load the Google tag once per visit so Google sees ordinary page views
  // (clears the "no tag pings" warning and improves click-to-lead matching).
  // Still blocked in consent regions by canTrackAds() inside loadGoogleAds.
  useEffect(() => {
    void loadGoogleAds();
  }, []);

  // SPA page views
  useEffect(() => {
    captureAdClick();
    trackEvent("page_view", {
      page_path: location.pathname + location.search,
      funnel_page: location.pathname,
    });
  }, [location.pathname, location.search]);

  // Safety net: re-send any lead whose delivery to the server was not
  // confirmed, and ask the server to retry anything still undelivered to
  // Housecall Pro. Server-side dedupe prevents duplicates.
  useEffect(() => {
    const isThankYou = location.pathname.startsWith("/thank-you");
    if (isThankYou) confirmAwaitingLead();
    const timer = window.setTimeout(() => {
      void flushPendingLeads().then(() => reportConfirmedLeads());
    }, 600);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);


  return null;
};

export default ConversionTracking;
